import { gql } from '@apollo/client';
import { nodeDataFragment } from '../fragments/nodes';
import { clonedFromJourneyFragment, workflowFragment } from '../fragments/workflows';

export const fetchWorkflowQuery = gql`
  query fetchWorkflowQuery($id: Long!) {
    workflow(id: $id) {
      ...WorkflowFragment
    }
  }
  ${workflowFragment}
`;

/**
 * Includes `workflowType` and `workflowReference` for
 * workflow drafts. Is only fired when the feature flag
 * is on.
 */
export const fetchWorkflowQueryV2 = gql`
  query fetchWorkflowQueryV2($id: Long!, $workflowType: WorkflowType) {
    workflow(id: $id, workflowType: $workflowType) {
      ...WorkflowFragment
    }
  }
  ${workflowFragment}
`;

export const fetchClonedFromJourney = gql`
  query fetchClonedFromJourney($id: Long!, $workflowType: WorkflowType) {
    workflow(id: $id, workflowType: $workflowType) {
      ...ClonedFromJourneyFragment
    }
  }
  ${clonedFromJourneyFragment}
`;

export const validateMultiContent = gql`
  query validateMultiContent($templateIds: [Long!]!) {
    validateMultiContent(templateIds: $templateIds) {
      errors {
        key
        value {
          __typename
        }
      }
    }
  }
`;

export const fetchJourneyNodeQuery = gql`
  query journeyNode($nodeId: Long!, $journeyType: WorkflowType!) {
    journeyNode(nodeId: $nodeId, journeyType: $journeyType) {
      clonedFromNode {
        resource {
          name
          ... on CampaignNodeResource {
            campaign {
              id
              conversions {
                events {
                  name
                }
              }
              customConversion {
                eventName
              }
            }
          }
          ... on MultiCampaignNodeResource {
            resources {
              campaign {
                id
                conversions {
                  events {
                    name
                  }
                }
                customConversion {
                  eventName
                }
              }
            }
          }
        }
        nodeData {
          ...NodeDataFragment
          ... on ChannelOptimizedSendAction {
            channels {
              key
              value {
                campaignId
              }
            }
          }
        }
        nodeDataJson
      }
    }
  }
  ${nodeDataFragment}
`;

/**
 * Field `aggregationType` has been deprecated
 * Field `attributionMode` has been deprecated
 */
export const fetchSendNodeResourcesQuery = gql`
  query fetchSendNodeResources($campaignId: Long!, $hasCampaign: Boolean = false) {
    campaign(id: $campaignId) @include(if: $hasCampaign) {
      campaignAlertsEnabled
      campaignState
      conversions {
        events {
          name
          aggregationType
          attributionMode {
            name: __typename
            ... on LastMessaged {
              periodHours
            }
          }
        }
      }
      customConversion {
        eventName
        attributionMode
        attributionPeriodHours
        valueField
      }
      labels {
        name
        id
      }
      customRateLimit {
        rateLimitPerMin
        useRateLimit
      }
      priorityLevel
      placementId
      embeddedPriorityOrder
      campaignTypeDefinition
      mobileAppIds
      schedulerMsgParams {
        defaultTimeZone
        scheduleType
        schedulerMsgIds
        startTimeZone
        windowHours
      }
      customQuietHours {
        startTime
        endTime
        sendSkip
      }
    }
    campaignLabels {
      id
      name
    }
    currentProject {
      id
      timeZone
    }
    locales {
      languageName
      tag
    }
  }
`;

export const fetchFacebookAdAccountIdsQuery = gql`
  query getFacebookAdAccountIds {
    facebookAdAccounts {
      id
      projectId
      accountId
    }
  }
`;

export const fetchMessageChannelsQuery = gql`
  query fetchMessageChannels {
    messageChannels {
      id
      name
      creatorUserId
      updatedAt
    }
  }
`;

export const fetchTemplateLocalesQuery = gql`
  query fetchTemplateLocales($templateId: Long!) {
    template(id: $templateId) {
      locales {
        languageName
        tag
      }
    }
  }
`;

export const workflowDiffQuery = gql`
  query workflowDraftDiff($workflow: WorkflowInputInput!) {
    workflowDraftDiff(workflow: $workflow) {
      added {
        liveWorkflowNodeId
        nodeDataJson
      }
      changed {
        liveWorkflowNodeId
        nodeDataJson
      }
      deleted {
        liveWorkflowNodeId
        nodeDataJson
      }
    }
  }
`;

export const fetchWorkflowCampaignNodesQuery = gql`
  query getWorkflowCampaignNodes(
    $workflowId: Long!
    $workflowType: WorkflowType!
    $nodeResources: [WorkflowNodeResourceFilter!]
  ) {
    workflow(id: $workflowId, workflowType: $workflowType, nodeResources: $nodeResources) {
      allNodes {
        resource {
          ... on CampaignNodeResource {
            campaign {
              id
              name
              templateId
              messageMedium
            }
          }
          ... on MultiCampaignNodeResource {
            resources {
              campaign {
                id
                name
                templateId
                messageMedium
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * `validateWorkflow` does not exist GraphQL schema at this time
 */
/* Disabled in preparation for graphql linting */
/* eslint-disable */
export const validateWorkflowQuery = gql`
  query validateWorkflow($workflow: WorkflowInputInput!) {
    validateWorkflow(workflow: $workflow) {
      isValid
      nodeValidationErrors {
        nodeId
        errors {
          error
          propertyName
        }
      }
    }
  }
`;

export const getJourneyVisitorMetrics = gql`
  query getJourneyVisitorMetrics($workflowId: Long!, $startTime: Instant!, $endTime: Instant!) {
    getJourneyVisitorMetrics(workflowId: $workflowId, startTime: $startTime, endTime: $endTime) {
      visitorsEntered
      visitorsFinished
      visitorsExitedByRules
      tileVisitorMetrics {
        nodeId
        visitorsExitedCount
        visitorsEnteredCount
        edgeVisitorPercentages {
          srcNodeId,
          destNodeId,
          srcOutputIdx,
          percentageOfVisitors,
        }
      }
    }
  }
  `;
